import { c as _c } from "react/compiler-runtime";
import { IconHelp } from "@tabler/icons-react";
import { HostingMap } from "@/components/domain/hosting/hosting-map";
import { KeyValue } from "@/components/domain/key-value";
import { KeyValueGrid } from "@/components/domain/key-value-grid";
import { ReportSection } from "@/components/domain/report-section";
import { ProviderLogo } from "@/components/icons/provider-logo";
import { sections } from "@/lib/constants/sections";
import type { HostingGeo, HostingResponse } from "@domainstack/types";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
import { countryCodeToEmoji } from "@domainstack/utils/country-emoji";
function formatLocation(geo: HostingGeo): string {
  const parts = [geo.city, geo.region, geo.country].filter(Boolean);
  return parts.join(", ");
}
function ProviderKeyValue(t0) {
  const $ = _c(6);
  const {
    label,
    provider
  } = t0;
  const t1 = provider?.name ?? "Not configured";
  let t2;
  if ($[0] !== provider) {
    t2 = provider?.id ? <ProviderLogo providerId={provider.id} providerName={provider.name} /> : undefined;
    $[0] = provider;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label || $[3] !== t1 || $[4] !== t2) {
    t3 = <KeyValue label={label} value={t1} leading={t2} />;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function HostingLocation(t0) {
  const $ = _c(15);
  const {
    geo,
    domain
  } = t0;
  const {
    lat,
    lon
  } = geo;
  let t1;
  if ($[0] !== geo) {
    t1 = formatLocation(geo);
    $[0] = geo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== geo.country || $[3] !== geo.country_code) {
    t2 = geo.country_code ? <span title={geo.country || geo.country_code} className="text-lg leading-none">{countryCodeToEmoji(geo.country_code)}</span> : undefined;
    $[2] = geo.country;
    $[3] = geo.country_code;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <KeyValue label="Location" value={t1} leading={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== domain || $[9] !== lat || $[10] !== lon) {
    t4 = lat && lon ? <div className="relative h-[280px] w-full"><HostingMap lat={lat} lon={lon} domain={domain} /></div> : null;
    $[8] = domain;
    $[9] = lat;
    $[10] = lon;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function EmptyHosting() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Empty className="border border-dashed"><EmptyHeader><EmptyMedia variant="icon"><IconHelp /></EmptyMedia><EmptyTitle>No hosting details available</EmptyTitle><EmptyDescription>We couldn't detect hosting, email, or DNS provider info. If the domain has no A/AAAA records or blocked headers, details may be unavailable.</EmptyDescription></EmptyHeader></Empty>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function HostingSection(t0) {
  const $ = _c(7);
  const {
    domain,
    data
  } = t0;
  const dnsProvider = data?.dnsProvider ?? null;
  const hostingProvider = data?.hostingProvider ?? null;
  const emailProvider = data?.emailProvider ?? null;
  const hasAnyProvider = Boolean(dnsProvider?.name || hostingProvider?.name || emailProvider?.name);
  const geolocation = data?.geo ?? null;
  let t1;
  if ($[0] !== dnsProvider || $[1] !== domain || $[2] !== emailProvider || $[3] !== geolocation || $[4] !== hasAnyProvider || $[5] !== hostingProvider) {
    t1 = <ReportSection {...sections.hosting}>{hasAnyProvider ? <><KeyValueGrid colsDesktop={3}><ProviderKeyValue label="DNS" provider={dnsProvider} /><ProviderKeyValue label="Hosting" provider={hostingProvider} /><ProviderKeyValue label="Email" provider={emailProvider} /></KeyValueGrid>{geolocation ? <HostingLocation geo={geolocation} domain={domain} /> : null}</> : <EmptyHosting />}</ReportSection>;
    $[0] = dnsProvider;
    $[1] = domain;
    $[2] = emailProvider;
    $[3] = geolocation;
    $[4] = hasAnyProvider;
    $[5] = hostingProvider;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}